import React, { useState } from 'react';
import CountrySelect from './CountrySelect';
import BrandSelect from './BrandSelect';
import { lookupFlagUrl, lookupAlpha3FromFlagUrl, formatTimeInput, parseTimeInput } from '../utils';
import { useT } from '../i18n';

const EditParticipantModal = ({ participant, brandsList, onSave, showTime, onClose, participants }) => {
  const t = useT();
  const [form, setForm] = useState({
    ident: participant.ident || '',
    brandLogo: participant.brandLogo || '',
    pilot: participant.pilot || '',
    pilotNat: lookupAlpha3FromFlagUrl(participant.pilotFlag),
    copilot: participant.copilot || '',
    copilotNat: lookupAlpha3FromFlagUrl(participant.copilotFlag),
    total: formatTimeInput(participant.total),
    competitor: participant.competitor || '',
    priorityPilot: participant.priorityPilot || false,
    choose: participant.choose !== undefined ? participant.choose : true,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (!form.ident) return;
    onSave({
      ...participant,
      ident: form.ident,
      brandLogo: form.brandLogo || '',
      pilot: form.pilot,
      pilotFlag: form.pilotNat ? lookupFlagUrl(form.pilotNat) : '',
      copilot: form.copilot,
      copilotFlag: form.copilotNat ? lookupFlagUrl(form.copilotNat) : '',
      total: parseTimeInput(form.total),
      competitor: form.competitor,
      priorityPilot: form.priorityPilot,
      choose: form.choose,
    });
    onClose();
  };

  const inpStyle = {
    width: '100%', background: 'transparent', border: 'none',
    borderBottom: '1px solid #aaa', outline: 'none',
    fontFamily: 'Verdana', fontSize: '1em', color: '#222',
    padding: '2px 0',
  };

  const labelStyle = { fontSize: '0.72em', color: '#888', display: 'block', marginBottom: '2px' };

  // Find index of editing participant
  const index = participants.findIndex(p => p.id === participant.id);
  // Determine if disabling is allowed
  let canDisableChoose = true;
  if (index === 0) {
    canDisableChoose = true;
  } else if (index > 0) {
    const prev = participants[index - 1];
    canDisableChoose = prev && prev.choose === false;
  }
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: '12px', padding: '1.5rem 1.75rem',
        width: '520px', maxWidth: '92vw',
        boxShadow: '0 8px 32px rgba(0,0,0,0.55)',
        fontFamily: 'Verdana', color: '#222',
      }} onClick={e => e.stopPropagation()}>

        <h5 style={{ margin: '0 0 1.2rem', fontFamily: 'Verdana', fontSize: '1.05em', fontWeight: 'bold' }}>
          {t('editParticipantTitle')}
        </h5>

        {/* Number + Brand row */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ flex: '0 0 5em' }}>
            <label style={labelStyle}>#</label>
            <input value={form.ident} onChange={e => set('ident', e.target.value)}
              placeholder='#' style={{ ...inpStyle, textAlign: 'center' }} autoFocus />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>{t('brand')}</label>
            <BrandSelect brandsList={brandsList} value={form.brandLogo} onChange={v => set('brandLogo', v)} />
          </div>
        </div>

        {/* Driver */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>{t('driver')}</label>
          <div style={{ marginBottom: '5px' }}>
            <CountrySelect value={form.pilotNat} onChange={v => set('pilotNat', v)} />
          </div>
          <input value={form.pilot} onChange={e => set('pilot', e.target.value)}
            placeholder={t('driverNamePlaceholder')} style={inpStyle} />
        </div>

        {/* Co-Driver */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>{t('coDriver')}</label>
          <div style={{ marginBottom: '5px' }}>
            <CountrySelect value={form.copilotNat} onChange={v => set('copilotNat', v)} />
          </div>
          <input value={form.copilot} onChange={e => set('copilot', e.target.value)}
            placeholder={t('coDriverNamePlaceholder')} style={inpStyle} />
        </div>

        {/* Team */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={labelStyle}>{t('team')}</label>
          <input value={form.competitor} onChange={e => set('competitor', e.target.value)}
            placeholder={t('teamNamePlaceholder')} style={inpStyle} />
        </div>

        {/* Choose */}
        <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type='checkbox'
            id='edit-choose'
            checked={form.choose}
            onChange={e => set('choose', e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: canDisableChoose ? 'pointer' : 'not-allowed', accentColor: '#4488cc' }}
            disabled={!canDisableChoose}
          />
          <label htmlFor='edit-choose' style={{ ...labelStyle, fontSize: '0.88em', color: '#222', marginBottom: 0, cursor: canDisableChoose ? 'pointer' : 'not-allowed' }}>{t('allowChooseStart')}</label>
        </div>

        {/* Priority Pilot */}
        <div style={{ marginBottom: showTime ? '0.85rem' : '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            type='checkbox'
            id='edit-priority-pilot'
            checked={form.priorityPilot}
            onChange={e => set('priorityPilot', e.target.checked)}
            style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#4488cc' }}
          />
          <label htmlFor='edit-priority-pilot' style={{ ...labelStyle, fontSize: '0.88em', color: '#222', marginBottom: 0, cursor: 'pointer' }}>{t('priorityPilot')}</label>
        </div>

        {/* Time */}
        {showTime && (
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={labelStyle}>{t('timeSec')}</label>
            <input type='text' value={form.total} onChange={e => set('total', e.target.value)}
              placeholder='0:00:00.00' style={{ ...inpStyle, width: '10em' }} />
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            background: 'none', border: '1px solid #bbb', borderRadius: '6px',
            padding: '0.4em 1.1em', cursor: 'pointer', fontFamily: 'Verdana', fontSize: '0.9em',
          }}>{t('cancel')}</button>
          <button onClick={handleSave} style={{
            background: '#4488cc', border: 'none', borderRadius: '6px',
            color: 'white', padding: '0.4em 1.1em', cursor: 'pointer',
            fontFamily: 'Verdana', fontSize: '0.9em',
          }}>{t('save')}</button>
        </div>

      </div>
    </div>
  );
};

export default EditParticipantModal;
